<template>
  <div class="milestone-timeline-card flex gap-4">
    <div class="milestone-timeline-card__rail flex flex-col items-center">
      <div
        class="milestone-timeline-card__dot shrink-0"
        :class="`milestone-timeline-card__dot--${display.ampel}`"
      />
      <div v-if="!isLast" class="milestone-timeline-card__line flex-1" />
    </div>

    <div
      class="milestone-timeline-card__content flex-1 pb-4 min-w-0"
      :data-testid="`milestone-timeline-card-${milestone.id}`"
    >
      <div class="milestone-timeline-card__panel">
        <div class="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <h3 class="milestone-timeline-card__title m-0">
            {{ milestone.title }}
          </h3>
          <div class="flex items-center gap-1 shrink-0 ml-auto">
            <span
              class="milestone-timeline-card__badge"
              :class="`milestone-timeline-card__badge--${display.ampel}`"
            >
              <span
                class="milestone-timeline-card__badge-dot"
                :class="`milestone-timeline-card__dot--${display.ampel}`"
              />
              {{ badgeLabel }}
            </span>
            <Button
              v-if="editable"
              icon="pi pi-pencil"
              severity="secondary"
              text
              rounded
              :aria-label="$t('ProjectManagement.edit')"
              @click="$emit('edit', milestone)"
            />
            <Button
              v-if="editable"
              icon="pi pi-check"
              severity="secondary"
              text
              rounded
              class="text-[var(--color-orange)]"
              :aria-label="$t('ProjectManagement.milestones.actions.markDone')"
              @click="$emit('mark-done', milestone)"
            />
            <Button
              v-if="editable"
              icon="pi pi-trash"
              severity="danger"
              text
              rounded
              :aria-label="$t('ProjectManagement.milestones.deleteTitle')"
              @click="$emit('delete', milestone)"
            />
          </div>
        </div>

        <div class="milestone-timeline-card__dates">
          <span>{{ targetLine }}</span>
          <span v-if="milestone.actualDate">· {{ actualLine }}</span>
        </div>

        <div class="milestone-timeline-card__meta">
          <span :class="deviationClass">{{ deviationText }}</span>
          <span class="milestone-timeline-card__meta-muted">
            ·
            <i class="pi pi-user text-xs" aria-hidden="true" />
            {{ responsibleLabel }}
          </span>
          <span class="milestone-timeline-card__meta-muted">
            ·
            <i class="pi pi-link text-xs" aria-hidden="true" />
            {{ constraintLine }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import {
  calculateMilestoneDisplayStatus,
  formatMilestoneDate,
} from '@/utils/milestoneDisplay';

export default {
  name: 'MilestoneTimelineCard',
  components: {
    Button,
  },
  props: {
    milestone: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    constraintCount: {
      type: Number,
      default: 0,
    },
    responsibleName: {
      type: String,
      default: '',
    },
  },
  emits: ['edit', 'delete', 'mark-done'],
  computed: {
    display() {
      return calculateMilestoneDisplayStatus(
        this.milestone.dueDate,
        this.milestone.actualDate,
      );
    },
    badgeLabel() {
      return this.$t(`UserProjects.milestones.filters.${this.display.ampel}`);
    },
    targetLine() {
      return this.$t('UserProjects.milestones.targetLine', {
        date: formatMilestoneDate(this.milestone.dueDate),
      });
    },
    actualLine() {
      return this.$t('UserProjects.milestones.actualLine', {
        date: formatMilestoneDate(this.milestone.actualDate),
      });
    },
    deviationText() {
      const { deviation } = this.display;
      if (deviation.key === 'dash') {
        return '–';
      }
      if (deviation.key === 'onTime') {
        return this.$t('UserProjects.milestones.deviation.onTime');
      }
      const suffix = deviation.count === 1 ? 'one' : 'other';
      return this.$t(`UserProjects.milestones.deviation.${deviation.key}_${suffix}`, {
        count: deviation.count,
      });
    },
    deviationClass() {
      if (this.display.deviation.type === 'red') {
        return 'milestone-timeline-card__deviation milestone-timeline-card__deviation--red';
      }
      if (this.display.deviation.type === 'green') {
        return 'milestone-timeline-card__deviation milestone-timeline-card__deviation--green';
      }
      return 'milestone-timeline-card__deviation milestone-timeline-card__deviation--neutral';
    },
    responsibleLabel() {
      return this.responsibleName || '–';
    },
    constraintLine() {
      return this.$t('UserProjects.milestones.constraintCount', {
        count: this.constraintCount,
      });
    },
  },
};
</script>

<style scoped>
.milestone-timeline-card__rail {
  width: 12px;
}

.milestone-timeline-card__dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  margin-top: 1.25rem;
  box-shadow: 0 0 0 4px var(--surface-ground, #f5f5f5);
}

.milestone-timeline-card__dot--green {
  background: #22c55e;
}

.milestone-timeline-card__dot--yellow {
  background: #eab308;
}

.milestone-timeline-card__dot--red {
  background: #ef4444;
}

.milestone-timeline-card__line {
  width: 1px;
  background: #e5e5e5;
  min-height: 1rem;
}

.milestone-timeline-card__panel {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 1rem;
  transition: box-shadow 0.15s ease;
}

.milestone-timeline-card__panel:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.milestone-timeline-card__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  min-width: 0;
}

.milestone-timeline-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}

.milestone-timeline-card__badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
}

.milestone-timeline-card__badge--green {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.milestone-timeline-card__badge--yellow {
  background: rgba(234, 179, 8, 0.12);
  color: #ca8a04;
}

.milestone-timeline-card__badge--red {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.milestone-timeline-card__dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-size: 13px;
  color: var(--color-disabled);
  margin-bottom: 0.5rem;
}

.milestone-timeline-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
}

.milestone-timeline-card__deviation {
  font-weight: 500;
}

.milestone-timeline-card__deviation--red {
  color: #dc2626;
}

.milestone-timeline-card__deviation--green {
  color: #16a34a;
}

.milestone-timeline-card__deviation--neutral {
  color: var(--color-disabled);
  font-weight: 400;
}

.milestone-timeline-card__meta-muted {
  color: var(--color-disabled);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
